import { Autocomplete, TextField } from "@mui/material";
import axios, { CancelTokenSource } from "axios";
import { isAfter } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import Modal from "react-responsive-modal";
import { useHistory } from "react-router";
import * as Yup from "yup";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import {
  buildVisitsReportDatesState,
  buildVisitsReportPayload,
} from "../../utils/buildVisitsReportPayload";
import getValidationErrors from "../../utils/getValidationError";
import { toPropertyListRowFields } from "../../utils/propertyListRowFormat";
import refreshToken from "../../utils/refreshToken";
import {
  ButtonsContainer,
  Content,
  DatesContainer,
  FieldContainer,
  FieldsAndDatesContainer,
  TitleContainer,
} from "./styles";

interface IModalVisitsPerProperty {
  open: boolean;
  functionToCloseModal(): void;
}

interface IPropertyOption {
  id: number;
  title?: string;
  address?: string;
}

const DATE_REGEX = /^\d{4}-(((0)[0-9])|((1)[0-2]))-([0-2][0-9]|(3)[0-1])$/;

function getPropertyLabel(property: IPropertyOption) {
  const detail = property.title || property.address || "";
  return detail ? `${property.id} - ${detail}` : String(property.id);
}

export default function ModalVisitsPerProperty({
  open,
  functionToCloseModal,
}: IModalVisitsPerProperty) {
  const history = useHistory();
  const { user, dateToExpires, access_token } = useAuth();
  const { addToast } = useToast();

  const [properties, setProperties] = useState<IPropertyOption[]>([]);
  const [selectedProperty, setSelectedProperty] =
    useState<IPropertyOption | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [initialDate, setInitialDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [loadingShowScreenRequest, setLoadingShowScreenRequest] =
    useState(false);
  const [loadingExcelRequest, setLoadingExcelRequest] = useState(false);

  const cancelTokenRef = useRef<CancelTokenSource | undefined>(undefined);

  const handleSearchProperties = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setProperties([]);
        return;
      }

      if (cancelTokenRef.current !== undefined) {
        cancelTokenRef.current.cancel("Operation canceled due to new request.");
      }

      cancelTokenRef.current = axios.CancelToken.source();

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${
          token ?? access_token
        }`;

        const response = await api.get("/api/adm/properties", {
          params: {
            page: 1,
            search: searchTerm,
          },
          cancelToken: cancelTokenRef.current?.token,
        });

        const listPayload = response.data?.data?.data;
        const list = Array.isArray(listPayload) ? listPayload : [];

        setProperties(
          list.map((item: Record<string, unknown>) => {
            const row = toPropertyListRowFields(item);
            return {
              id: Number(item.id),
              title: typeof item.title === "string" ? item.title : undefined,
              address: row.address || undefined,
            };
          })
        );
      } catch (err: any) {
        if (axios.isCancel(err)) {
          return;
        }

        if (err?.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }
    },
    [access_token, addToast, dateToExpires]
  );

  const validateAndBuildPayload = async (type: "screen" | "excel") => {
    if (!user) {
      history.push("/");
      return null;
    }

    if (
      initialDate &&
      finalDate &&
      isAfter(new Date(initialDate), new Date(finalDate))
    ) {
      addToast({
        title: "Coloque datas válidas!",
        type: "error",
      });
      return null;
    }

    const data = {
      property_id: selectedProperty?.id,
      initial_date: initialDate || undefined,
      final_date: finalDate || undefined,
    };

    const schema = Yup.object().shape({
      property_id: Yup.number().required("Selecione um imóvel!"),
      initial_date: Yup.string()
        .notRequired()
        .test("valid-initial-date", "Coloque uma data válida!", (value) => {
          if (!value) return true;
          return DATE_REGEX.test(value);
        }),
      final_date: Yup.string()
        .notRequired()
        .test("valid-final-date", "Coloque uma data válida!", (value) => {
          if (!value) return true;
          return DATE_REGEX.test(value);
        }),
    });

    await schema.validate(data, {
      abortEarly: false,
    });

    return buildVisitsReportPayload({
      filterKey: "property_id",
      filterId: selectedProperty!.id,
      type,
      initialDate: initialDate || undefined,
      finalDate: finalDate || undefined,
    });
  };

  const handleShowReport = async () => {
    setLoadingShowScreenRequest(true);

    try {
      const payload = await validateAndBuildPayload("screen");
      if (!payload) {
        setLoadingShowScreenRequest(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(
        "/api/adm/reports/schedulesByProperty",
        payload
      );

      if (response.data.success) {
        history.push({
          pathname: "/show-visits-per-consultant-report",
          state: {
            data: response.data.data,
            dates: buildVisitsReportDatesState(
              initialDate || undefined,
              finalDate || undefined
            ),
            title: "Visitas por Imóvel",
            filterLabel: selectedProperty
              ? getPropertyLabel(selectedProperty)
              : undefined,
          },
        });

        functionToCloseModal();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
      } else if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingShowScreenRequest(false);
  };

  const handleExcelDownloadReport = async () => {
    setLoadingExcelRequest(true);

    try {
      const payload = await validateAndBuildPayload("excel");
      if (!payload) {
        setLoadingExcelRequest(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(
        "/api/adm/reports/schedulesByProperty",
        payload,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Visitas por imovel.xlsx");
      link.click();
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
      } else if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingExcelRequest(false);
  };

  return (
    <Modal
      open={open}
      onClose={functionToCloseModal}
      classNames={{
        modal: "modalVisitsPerConsultant",
      }}
      closeIcon={<FiX size={18} color="#000" />}
    >
      <Content>
        <TitleContainer>
          <h3>VISITAS POR IMÓVEL</h3>
        </TitleContainer>

        <FieldsAndDatesContainer>
          <FieldContainer>
            <p>Imóvel:</p>
            <Autocomplete
              options={properties}
              value={selectedProperty}
              inputValue={searchInput}
              getOptionLabel={(option) => getPropertyLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="Sem opções"
              style={{ width: "100%" }}
              onChange={(_event, value) => {
                setSelectedProperty(value);
                if (value) {
                  setSearchInput(getPropertyLabel(value));
                }
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === "input") {
                  setSearchInput(value);
                  handleSearchProperties(value);
                }
                if (reason === "clear") {
                  setSearchInput("");
                  setSelectedProperty(null);
                  setProperties([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  placeholder="Buscar por ID ou endereço"
                />
              )}
            />
          </FieldContainer>

          <DatesContainer>
            <p>Data inicial:</p>
            <TextField
              id="initial-date"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              value={initialDate}
              onChange={(event) => setInitialDate(event.target.value)}
            />

            <p>Data final:</p>
            <TextField
              id="final-date"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              value={finalDate}
              onChange={(event) => setFinalDate(event.target.value)}
            />
          </DatesContainer>
        </FieldsAndDatesContainer>

        <ButtonsContainer>
          <button
            onClick={
              loadingExcelRequest ? () => {} : handleExcelDownloadReport
            }
          >
            {loadingExcelRequest ? "Carregando..." : "Excel"}
          </button>
          <button
            onClick={loadingShowScreenRequest ? () => {} : handleShowReport}
          >
            {loadingShowScreenRequest ? "Carregando..." : "Visualizar"}
          </button>
        </ButtonsContainer>
      </Content>
    </Modal>
  );
}
